import { ErrorBoundary } from '@/components/ErrorBoundary';
import { useSecuritiesOverview } from '@/hooks/securities/useSecuritiesOverview.ts';
import { useSecuritiesCurrencyConversion } from '@/hooks/securities/useSecuritiesCurrencyConversion.ts';
import { SecuritiesErrorState } from '@/components/securities-error-state/SecuritiesErrorState';
import { useMemo, useState } from 'react';
import { 
  PageHeaderSkeleton, 
  PortfolioSummarySkeleton, 
  DistributionChartSkeleton,
  AccountCardSkeleton 
} from '@/components/LoadingSkeletons';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, TrendingUp, FileText, BarChart3, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { CurrencyToggle } from '@/components/currency-toggle/CurrencyToggle';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { Link } from 'react-router-dom';

/**
 * SecuritiesOverviewPage component displays aggregate metrics for all securities investments.
 * This is the main securities page accessible at /investments/securities.
 */
export default function SecuritiesOverviewPage() {
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch securities overview data
  const { 
    data: overview, 
    isLoading, 
    error,
    refetch,
  } = useSecuritiesOverview(searchQuery);

  // Base currency defaults to USD
  const baseCurrency = 'USD';

  // Currency conversion hook - follows Money Market Funds pattern
  const {
    selectedCurrency,
    setSelectedCurrency,
    convertedValues,
    isConverting,
    conversionError,
    conversionFailed,
    usingFallback,
  } = useSecuritiesCurrencyConversion({
    baseCurrency,
    values: {
      totalValue: overview?.totalValue ?? 0,
      equityValue: overview?.equityValue ?? 0,
      debtValue: overview?.debtValue ?? 0,
      derivativesValue: overview?.derivativesValue ?? 0,
    },
  });

  // Prepare distribution chart data using converted values
  const distributionData = useMemo(() => {
    if (!overview) return [];
    
    return [
      { name: 'Equity', value: convertedValues.equityValue, count: overview.counts.equity },
      { name: 'Debt Securities', value: convertedValues.debtValue, count: overview.counts.debt },
      { name: 'Derivatives', value: convertedValues.derivativesValue, count: overview.counts.derivatives },
    ].filter(item => item.value > 0);
  }, [overview, convertedValues]);

  // Chart colors
  const COLORS = ['#3b82f6', '#10b981', '#f59e0b'];

  // Show loading skeletons while data is loading
  if (isLoading) {
    return (
      <ErrorBoundary
        onReset={() => {
          window.location.reload();
        }}
      >
        <div className="space-y-6" aria-busy="true" aria-live="polite">
          <PageHeaderSkeleton />
          <div className="grid gap-4 sm:gap-6 grid-cols-1 lg:grid-cols-2">
            <PortfolioSummarySkeleton />
            <DistributionChartSkeleton />
          </div>
          <div className="grid gap-4 sm:gap-6 grid-cols-1 md:grid-cols-3">
            <AccountCardSkeleton />
            <AccountCardSkeleton />
            <AccountCardSkeleton />
          </div>
        </div>
      </ErrorBoundary>
    );
  }

  // Show error state
  if (error) {
    return (
      <ErrorBoundary onReset={() => { window.location.reload(); }}>
        <div className="space-y-6">
          <SecuritiesErrorState
            message="Failed to load securities data. Please try again."
            onRetry={() => refetch()}
          />
        </div>
      </ErrorBoundary>
    );
  }

  const hasSecurities = overview && overview.totalValue > 0;

  return (
    <ErrorBoundary
      onReset={() => {
        window.location.reload();
      }}
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Securities Overview</h1>
            <p className="text-sm text-muted-foreground sm:text-base">
              View your complete securities portfolio
            </p>
          </div>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center mt-4 lg:mt-0">
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search securities..."
                className="pl-8"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="flex items-center" role="group" aria-label="Securities overview actions">
              {/* Currency Toggle */}
              <CurrencyToggle
                baseCurrency={baseCurrency}
                selectedCurrency={selectedCurrency}
                onCurrencyChange={setSelectedCurrency}
                balance={convertedValues.totalValue}
                showBalance={false}
              />
            </div>
          </div>
        </div>

        {/* Currency Conversion Loading Indicator */}
        {isConverting && (
          <Alert role="status" aria-live="polite" aria-busy="true">
            <AlertDescription>
              Converting to {selectedCurrency}...
            </AlertDescription>
          </Alert>
        )}

        {/* Currency Conversion Warning - shown when conversion failed or using stale cache */}
        {selectedCurrency !== 'USD' && (conversionFailed || usingFallback) && (
          <Alert variant="destructive" role="alert" aria-live="assertive">
            <AlertCircle className="h-4 w-4" aria-hidden="true" />
            <AlertDescription>
              Currency conversion unavailable. Displaying amounts in original currencies.
            </AlertDescription>
          </Alert>
        )}

        {/* Total Portfolio Value and Distribution Chart */}
        <div className="grid gap-4 sm:gap-6 grid-cols-1 lg:grid-cols-2">
          {/* Total Portfolio Value Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Total Portfolio Value</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-muted-foreground mb-2">Total Value</p>
                <p className="text-3xl font-bold">
                  {selectedCurrency} {convertedValues.totalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Equity Securities</span>
                  <span className="text-sm font-medium">
                    {selectedCurrency} {convertedValues.equityValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Debt Securities</span>
                  <span className="text-sm font-medium">
                    {selectedCurrency} {convertedValues.debtValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Derivatives</span>
                  <span className="text-sm font-medium">
                    {selectedCurrency} {convertedValues.derivativesValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Distribution Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Portfolio Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              {hasSecurities ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={distributionData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {distributionData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value: number) => `${selectedCurrency} ${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                  <p>No securities data to display</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Summary Cards for Each Security Type */}
        <div className="grid gap-4 sm:gap-6 grid-cols-1 md:grid-cols-3">
          {/* Equity Card */}
          <Link to="/investments/securities/equity" className="block">
            <Card className="cursor-pointer hover:bg-accent/50 transition-colors h-full">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                    <TrendingUp className="h-6 w-6 text-blue-600 dark:text-blue-400" aria-hidden="true" />
                  </div>
                  <Button variant="ghost" size="sm" asChild>
                    <span className="text-sm">View Details →</span>
                  </Button>
                </div>
                <h3 className="text-lg font-semibold mb-2">Equity Securities</h3>
                <p className="text-2xl font-bold mb-1">
                  {selectedCurrency} {convertedValues.equityValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
                <p className="text-sm text-muted-foreground">
                  {overview?.counts.equity || 0} {overview?.counts.equity === 1 ? 'holding' : 'holdings'}
                </p>
              </CardContent>
            </Card>
          </Link>

          {/* Debt Securities Card */}
          <Link to="/investments/securities/debt-securities" className="block">
            <Card className="cursor-pointer hover:bg-accent/50 transition-colors h-full">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                    <FileText className="h-6 w-6 text-green-600 dark:text-green-400" aria-hidden="true" />
                  </div>
                  <Button variant="ghost" size="sm" asChild>
                    <span className="text-sm">View Details →</span>
                  </Button>
                </div>
                <h3 className="text-lg font-semibold mb-2">Debt Securities</h3>
                <p className="text-2xl font-bold mb-1">
                  {selectedCurrency} {convertedValues.debtValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
                <p className="text-sm text-muted-foreground">
                  {overview?.counts.debt || 0} {overview?.counts.debt === 1 ? 'bond' : 'bonds'}
                </p>
              </CardContent>
            </Card>
          </Link>

          {/* Derivatives Card */}
          <Link to="/investments/securities/derivatives" className="block">
            <Card className="cursor-pointer hover:bg-accent/50 transition-colors h-full">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="p-2 bg-amber-100 dark:bg-amber-900 rounded-lg">
                    <BarChart3 className="h-6 w-6 text-amber-600 dark:text-amber-400" aria-hidden="true" />
                  </div>
                  <Button variant="ghost" size="sm" asChild>
                    <span className="text-sm">View Details →</span>
                  </Button>
                </div>
                <h3 className="text-lg font-semibold mb-2">Derivatives</h3>
                <p className="text-2xl font-bold mb-1">
                  {selectedCurrency} {convertedValues.derivativesValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
                <p className="text-sm text-muted-foreground">
                  {overview?.counts.derivatives || 0} {overview?.counts.derivatives === 1 ? 'contract' : 'contracts'}
                </p>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Empty State */}
        {!hasSecurities && (
          <Card role="status" aria-label="Empty state">
            <CardContent className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <p className="text-base sm:text-lg text-muted-foreground mb-4">
                No securities investments yet
              </p>
              <p className="text-xs sm:text-sm text-muted-foreground mb-6">
                Get started by adding your first security investment
              </p>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button asChild className="min-h-[44px] w-full sm:w-auto">
                  <Link to="/investments/securities/equity">Add Equity</Link>
                </Button>
                <Button asChild variant="outline" className="min-h-[44px] w-full sm:w-auto">
                  <Link to="/investments/securities/debt-securities">Add Bond</Link>
                </Button>
                <Button asChild variant="outline" className="min-h-[44px] w-full sm:w-auto">
                  <Link to="/investments/securities/derivatives">Add Derivative</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </ErrorBoundary>
  );
}
